import { fetchAuthSession } from 'aws-amplify/auth';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { AwsCredentialIdentity } from '@aws-sdk/types';
import ENVIRONMENT_VARIABLES from 'src/constants/environment-variables';

/** The current Cognito ID token (refreshed by Amplify when needed). Throws when there is none. */
export const getToken = async () => {
  const currentSession = await fetchAuthSession();
  const token = currentSession.tokens?.idToken?.toString();
  if (!token) throw new Error('No ID token found in session');
  return token;
};

const getCognitoDomain = () => {
  return 'cognito-idp.' + ENVIRONMENT_VARIABLES.env.Region + '.amazonaws.com/' + ENVIRONMENT_VARIABLES.env.CognitoUserPoolId;
};

// Cached resolved credentials — avoids repeated Cognito+STS round-trips across all AWS service clients
let cachedCredentials: AwsCredentialIdentity | null = null;
let credentialPromise: Promise<AwsCredentialIdentity> | null = null;

const isExpired = (creds: AwsCredentialIdentity): boolean => {
  if (!creds.expiration) return false;
  // Refresh 5 minutes before actual expiry to avoid mid-request failures
  return creds.expiration.getTime() - Date.now() < 5 * 60 * 1000;
};

export async function getAWSServiceCredentials(): Promise<AwsCredentialIdentity> {
  if (cachedCredentials && !isExpired(cachedCredentials)) return cachedCredentials;

  // Clear stale cache
  cachedCredentials = null;

  // Deduplicate concurrent calls during initial resolution
  if (!credentialPromise) {
    credentialPromise = (async () => {
      try {
        const COGNITO_LOGIN: Record<string, string> = {};
        COGNITO_LOGIN[getCognitoDomain()] = await getToken();
        const resolved = await fromCognitoIdentityPool({
          clientConfig: { region: ENVIRONMENT_VARIABLES.env.Region },
          identityPoolId: ENVIRONMENT_VARIABLES.env.CognitoIdentityPoolId,
          logins: COGNITO_LOGIN
        })();
        cachedCredentials = resolved;
        return resolved;
      } finally {
        credentialPromise = null;
      }
    })();
  }

  return credentialPromise;
}
